/**
 * Detect newly deployed UI builds and show a sticky refresh prompt.
 *
 * Compares the version baked into THIS running JS vs live /version.json
 * (must be network-only — see vite PWA globIgnores / NetworkOnly).
 *
 * Only prompt when versions actually differ. After Refresh loads the new
 * build, the toast must not come back for the same version.
 */
import { h } from 'vue'
import { toast } from '@/shared/components/ui/toast/use-toast'
import { clearUiUpdateNotice, setUiUpdateNotice } from '@/core/app/uiUpdateNotice'

const STORAGE_KEY = 'fcrm_ui_version'
const CHECK_MS = 60_000

declare const __APP_UI_VERSION__: string

function embeddedVersion(): string {
  try {
    return String(__APP_UI_VERSION__ || '').trim()
  } catch {
    return ''
  }
}

function displayVersion(v: string): string {
  const s = String(v || '').trim()
  return s.split('+')[0] || s || '?'
}

function versionsEqual(a: string, b: string): boolean {
  return String(a || '').trim() === String(b || '').trim()
}

/** Network fetch — SW is configured NetworkOnly for this URL. */
async function fetchRemoteVersion(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?_=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    })
    if (!res.ok) return null
    const data = await res.json()
    const v = String(data?.version || '').trim()
    return v || null
  } catch {
    return null
  }
}

/** Bust SW + Cache Storage only — never clear localStorage (keeps token/user). */
async function bustHttpAndServiceWorkerCaches() {
  try {
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k)))
    }
  } catch {
    /* ignore */
  }

  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((r) => r.unregister()))
    }
  } catch {
    /* ignore */
  }
}

let toastOpen = false
let dismissUpdateToast: (() => void) | null = null

function removeHtmlUpdateBar() {
  try {
    document.getElementById('fcrm-ui-update-bar')?.remove()
  } catch {
    /* ignore */
  }
}

/** Clear toast + header notice when already on the live build. */
function clearUpdatePrompt() {
  clearUiUpdateNotice()
  removeHtmlUpdateBar()
  if (dismissUpdateToast) {
    try {
      dismissUpdateToast()
    } catch {
      /* ignore */
    }
  }
  dismissUpdateToast = null
  toastOpen = false
}

function markSynced(version: string) {
  try {
    localStorage.setItem(STORAGE_KEY, version)
  } catch {
    /* ignore */
  }
  clearUpdatePrompt()
}

/** Hard refresh without logout — same idea as Ctrl+Shift+R. */
export async function applyUiUpdateReload(nextVersion?: string | null) {
  const v = String(nextVersion || '').trim()
  if (v) markSynced(v)
  else clearUpdatePrompt()

  await bustHttpAndServiceWorkerCaches()

  const url = new URL(window.location.href)
  url.searchParams.set('_ui', String(Date.now()))
  window.location.replace(url.toString())
}

/** @deprecated use applyUiUpdateReload */
export async function applyUiUpdateAndSignIn(nextVersion?: string | null) {
  return applyUiUpdateReload(nextVersion)
}

function showUpdateToast(remoteVersion: string) {
  const runningFull = embeddedVersion()

  // Already running the live build → never show / clear leftover UI.
  if (!remoteVersion || !runningFull || versionsEqual(runningFull, remoteVersion)) {
    markSynced(remoteVersion || runningFull)
    return
  }

  setUiUpdateNotice({
    remoteVersion,
    localVersion: runningFull,
  })

  if (toastOpen) return

  removeHtmlUpdateBar()

  toastOpen = true
  const result = toast({
    title: 'New version available',
    description: `Version ${displayVersion(remoteVersion)} is ready. Refresh to update.`,
    duration: 0,
    persistent: true,
    onOpenChange: (open: boolean) => {
      if (!open) {
        clearUpdatePrompt()
      }
    },
    action: h(
      'button',
      {
        type: 'button',
        class:
          'inline-flex items-center justify-center rounded-md text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground h-8 px-3 shrink-0 cursor-pointer',
        onClick: async () => {
          await applyUiUpdateReload(remoteVersion)
        },
      },
      'Refresh'
    ),
  })
  dismissUpdateToast = () => {
    result.dismiss()
    toastOpen = false
    dismissUpdateToast = null
  }
}

async function checkForUiUpdate() {
  const local = embeddedVersion()
  const remote = await fetchRemoteVersion()
  if (!remote || !local) return

  if (versionsEqual(local, remote)) {
    markSynced(remote)
    return
  }

  showUpdateToast(remote)
}

/**
 * Call once after app mount. Re-checks on focus, visibility, interval, and SW updates.
 */
export function startUiVersionWatcher(_opts?: {
  updateSW?: ((reload?: boolean) => Promise<void>) | null
}) {
  void checkForUiUpdate()

  const onVis = () => {
    if (document.visibilityState === 'visible') void checkForUiUpdate()
  }
  document.addEventListener('visibilitychange', onVis)
  window.addEventListener('focus', onVis)

  const intervalId = window.setInterval(() => {
    void checkForUiUpdate()
  }, CHECK_MS)

  // PWA may fire this even when app version matches — only prompt if behind.
  const onNeedRefresh = () => {
    void checkForUiUpdate()
  }
  window.addEventListener('fcrm-ui-need-refresh', onNeedRefresh)

  return () => {
    document.removeEventListener('visibilitychange', onVis)
    window.removeEventListener('focus', onVis)
    window.removeEventListener('fcrm-ui-need-refresh', onNeedRefresh)
    window.clearInterval(intervalId)
  }
}

/** Fire from PWA onNeedRefresh — watcher re-checks versions before prompting. */
export function notifyUiNeedsRefresh() {
  window.dispatchEvent(new Event('fcrm-ui-need-refresh'))
}
